import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';

import * as WorkflowStepFunctionActions from 'libs/project/src/lib/states/workflow-step-function/workflow-step-function.actions';
import * as WorkflowEventActions from 'libs/project/src/lib/states/workflow-event/workflow-event.actions';
import { WorkflowStepFunctionMode, WorkflowStepFunctionType, WorkflowStepFunctionTypeOption } from '../utils/diagram-configs';
import { AiFunctionCall, AiFunctionCallControllerService, CustomFunctionDefinitionControllerService, WorkflowStepFunctionWithRelations } from '@rappider/rappider-sdk';
import { catchError, map, mergeMap, of } from 'rxjs';
import { StoreKeys, SyncRemoteDataService } from '@rappider/services';
import { ProgrammingLanguage } from 'libs/custom-function/src/lib/utils/programming-languages';

import { v4 } from 'uuid';


@Injectable({
  providedIn: 'root'
})
export class WorkflowService {

  constructor(
    private store: Store<any>,
    private syncRemoteDataService: SyncRemoteDataService,
    private aiFunctionCallControllerService: AiFunctionCallControllerService,
    private customFunctionDefinitionControllerService: CustomFunctionDefinitionControllerService
  ) { }
  /**
   * Generates an empty step function based on the provided type.
   * @param workflowStepFunctionType The type of the step function to generate.
   * @param placeholderDataId Optional ID for placeholder data.
   */
  generateEmptyStepFunction(
    activeProjectId: string,
    workflowStepFunctionType: WorkflowStepFunctionTypeOption,
    placeholderDataId?: string,
    subscribedEventIds?: string[], // IDs of events to subscribe to
    workflowStepFunctions?: WorkflowStepFunctionWithRelations[], // existing step functions, if any
    aiFunctions?: AiFunctionCall[], // existing AI functions, if any
    generateFailureEvent: boolean = false
  ): any {

    const stepFunctionId = v4(); // Generate a unique ID for the step function

    if (!activeProjectId) {
      throw new Error('Active project ID is required to generate a step function');
    }

    const draftStepFunctionParams = {
      workflowStepFunction: {
        id: stepFunctionId,
        name: this.getNextAvailableUniqueFieldStringValue(workflowStepFunctions, 'name', 'newStepFunction'),
        description: '',
        mode: WorkflowStepFunctionMode.CustomFunctionDefinition,
        endpointId: null,
        customFunctionDefinitionId: null,
        customOrEndpoint: null,
        customCode: null,
        projectModel: null
      },
      publishedEventOnFailureIds: [],
      publishedEventOnSuccessIds: [],
      subscribedEventIds: subscribedEventIds || [], // Initialize with provided event IDs or empty array
    } as any;

    const draftFailureEventPayload = {
      workflowEvent: {
        name: draftStepFunctionParams.workflowStepFunction.name + 'Failure',
        inputDataSample: { error: 'An error occurred' },
        eventType: 'failure'
      },
      placeholderData: {
        id: v4(),
        eventId: v4(), // Generate a unique ID for the event
        stepFunctionId: stepFunctionId,
        type: 'failure' as 'success' | 'failure' | 'subscribe'
      }
    };

    const draftSuccessEventPayload = {
      workflowEvent: {
        name: draftStepFunctionParams.workflowStepFunction.name + 'Success',
        inputDataSample: { result: 'Success' },
        eventType: 'success'
      },
      placeholderData: {
        id: v4(),
        eventId: v4(), // Generate a unique ID for the event
        stepFunctionId: stepFunctionId,
        type: 'success' as 'success' | 'failure' | 'subscribe'
      }
    };

    let workflowStepFunctionMode = WorkflowStepFunctionMode.CustomFunctionDefinition;

    switch (workflowStepFunctionType) {
      case WorkflowStepFunctionTypeOption.AI:
        workflowStepFunctionMode = WorkflowStepFunctionMode.AiFunction;
        break;

      case WorkflowStepFunctionTypeOption.RouteWithConditions:
        workflowStepFunctionMode = WorkflowStepFunctionMode.CustomFunctionDefinition;
        break;

      case WorkflowStepFunctionTypeOption.BlankFunction:
        workflowStepFunctionMode = WorkflowStepFunctionMode.CustomFunctionDefinition;
        break;
      case WorkflowStepFunctionTypeOption.ProjectEndpoints:
        workflowStepFunctionMode = WorkflowStepFunctionMode.Endpoint;
        break;
      default:
        throw new Error('Invalid workflow step function type');
    }

    if (workflowStepFunctionMode === WorkflowStepFunctionMode.AiFunction) {
      // If the step function type is AI, we need to create an AI function call then add it to the step function

      draftStepFunctionParams.workflowStepFunction.mode = WorkflowStepFunctionMode.AiFunction;
      const draftAIFunctionCallParams = {
        body: {
          name: this.getNextAvailableUniqueFieldStringValue(aiFunctions, 'name', 'NewAIFunction'),
          title: 'AI Function for ' + draftStepFunctionParams.workflowStepFunction.name,
          description: 'AI Function for ' + draftStepFunctionParams.workflowStepFunction.name,
          exposeAsFeature: false,
          systemPrompt: 'generate me a random but meaningful sentence',
          responseJsonSample: {
            text: 'Hello Rapider!'
          },
          temperature: 0.2,
          index: aiFunctions?.length || 0,
          projectId: activeProjectId
        }
      };
      this.aiFunctionCallControllerService.create$Json(draftAIFunctionCallParams).subscribe(
        (aiFunctionCall: AiFunctionCall) => {
          if (aiFunctionCall.id) {
            draftStepFunctionParams.workflowStepFunction.aiFunctionId = aiFunctionCall.id;

            // Create the Step Function
            this.store.dispatch(WorkflowStepFunctionActions.CreateWorkflowStepFunction({
              payload: {
                workflowStepFunction: draftStepFunctionParams,
                placeholderDataId: placeholderDataId,
              }
            }));
            // Create the failure event, optionally
            if (generateFailureEvent) {
              this.store.dispatch(WorkflowEventActions.CreateWorkflowEvent({ payload: draftFailureEventPayload }));
            }
            // Create the success event
            this.store.dispatch(WorkflowEventActions.CreateWorkflowEvent({ payload: draftSuccessEventPayload }));

          } else {
            console.error('AI Function Call creation failed: No ID returned');
          }
        },
        (error) => {
          console.error('Error creating AI Function Call:', error);
        }
      );
    } else if (workflowStepFunctionMode === WorkflowStepFunctionMode.Endpoint) {
      // If the step function type is Project Endpoints, we need to set the mode to Endpoint
      draftStepFunctionParams.workflowStepFunction.mode = WorkflowStepFunctionMode.Endpoint;
      draftStepFunctionParams.workflowStepFunction.endpointId = null; // Set endpointId to null for now

      // Create the Step Function
      this.store.dispatch(WorkflowStepFunctionActions.CreateWorkflowStepFunction({
        payload: {
          workflowStepFunction: draftStepFunctionParams,
          placeholderDataId: placeholderDataId,
        }
      }));
      // Create the failure event
      if (generateFailureEvent) {
        this.store.dispatch(WorkflowEventActions.CreateWorkflowEvent({ payload: draftFailureEventPayload }));
      }
      // Create the success event
      this.store.dispatch(WorkflowEventActions.CreateWorkflowEvent({ payload: draftSuccessEventPayload }));

    } else if (workflowStepFunctionMode === WorkflowStepFunctionMode.CustomFunctionDefinition) {
      // If the step function type is Custom Function Definition, we need to create a custom function definition
      draftStepFunctionParams.workflowStepFunction.mode = WorkflowStepFunctionMode.CustomFunctionDefinition;

      const stepFunctionName = draftStepFunctionParams.workflowStepFunction.name || 'newStepFunction';

      // generate the custom function definition
      this.customFunctionDefinitionControllerService.create({
        body: {
          title: `Custom Function for ${stepFunctionName}`,
          serviceName: 'WorkflowFunctions',
          description: `Step Function code for ${stepFunctionName}`,
          functionName: `stepFunctionCodeFor${stepFunctionName}`,
          programmingLanguage: ProgrammingLanguage.NodeJs_20,
          code: '...', // Placeholder for custom code
        }
      }).subscribe(
        (customFunctionDefinition: any) => {
          if (customFunctionDefinition.id) {
            draftStepFunctionParams.workflowStepFunction.customFunctionDefinitionId = customFunctionDefinition.id;
            draftStepFunctionParams.workflowStepFunction.customOrEndpoint = customFunctionDefinition;

            // Create the Step Function
            this.store.dispatch(WorkflowStepFunctionActions.CreateWorkflowStepFunction({
              payload: {
                workflowStepFunction: draftStepFunctionParams,
                placeholderDataId: placeholderDataId,
              }
            }));

            // Create the failure event
            if (generateFailureEvent) {
              this.store.dispatch(WorkflowEventActions.CreateWorkflowEvent({ payload: draftFailureEventPayload }));
            }
            // Create the success event
            this.store.dispatch(WorkflowEventActions.CreateWorkflowEvent({ payload: draftSuccessEventPayload }));

          } else {
            console.error('Custom Function Definition creation failed: No ID returned');
          }
        },
        (error) => {
          console.error('Error creating Custom Function Definition:', error);
        }
      );
    }
  }

  getAiFunctionCalls() {
    return this.aiFunctionCallControllerService.find().pipe(
      map((aiFunctionCalls: AiFunctionCall[]) => aiFunctionCalls || []),
      catchError((error) => {
        console.error('Error fetching AI Function Calls:', error);
        return of([]); // Return an empty array on error
      })
    );
  }

  /**
   * Generates a unique string value for a field in the provided data array.
   * The function checks existing values in the specified field and appends a counter
   * to the seed value until a unique value is found.
   *
   * @param data - Array of objects containing the field to check for uniqueness.
   * @param fieldNameThatNeedsUniqueValue - The name of the field that needs a unique value.
   * @param valueSeedString - The base string to use for generating the unique value.
   * @returns A unique string value for the specified field.
   */
  getNextAvailableUniqueFieldStringValue(data: any[], fieldNameThatNeedsUniqueValue?: string, valueSeedString?: string): string {
    const seedValue = valueSeedString || 'New';

    if (!data?.length) {
      return seedValue;
    }

    const existingNames = data?.map(item => item[fieldNameThatNeedsUniqueValue || 'name']);
    let nextName = seedValue;
    let counter = 1;

    while (existingNames.includes(nextName)) {
      nextName = `${seedValue}${counter}`;
      counter++;
    }

    return nextName;
  }

  refreshSyncedRemoteData(activeProjectId: string | null = null): void {
    if (activeProjectId) {
      this.syncRemoteDataService.syncRemoteDataItemWithKey(activeProjectId, StoreKeys.AIFunctionCall);
      this.syncRemoteDataService.syncRemoteDataItemWithKey(activeProjectId, StoreKeys.WorkflowEvent);
      this.syncRemoteDataService.syncRemoteDataItemWithKey(activeProjectId, StoreKeys.WorkflowStepFunction);
      this.syncRemoteDataService.syncRemoteDataItemWithKey(activeProjectId, StoreKeys.WorkflowStepFunctionTemplate);
      this.syncRemoteDataService.syncRemoteDataItemWithKey(activeProjectId, StoreKeys.TemplateWorkflowFunctions);
    }
  }

}
